import { useState, useMemo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import CountrySelect from "@stadium/ui/src/components/country-select";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { getCountriesGroupedByZoneUsingCountryZoneId } from "@stadium/redux-utils/selectors/configSelector";

import { InternalButton } from "@sharedComponents/buttons";

import useCountryChange from "@hooks/use-country-change";

import { getCountry } from "@selectors/country";
import { getCartOrderId, getCartProductZoneList } from "@selectors/cart";

import { updateOrderDetails } from "@actions/order";
import { updateCartOrder } from "@slices/cart";

import Modal from "@stadium/ui/src/components/ModalNew";
import {
  ModalHeader,
  ModalFooter,
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
} from "@stadium/ui/src/components/ModalNew/utils";

import { useSendItemsContext } from "@contexts/send-items-context";
import { useCartListingContext } from "@contexts/cart-listing-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import useAllCountries from "@hooks/countries/use-all-countries";

import { formatOrder } from "@stadium/checkout/src/helpers/order";

import { SM_PROGRAM_ID } from "@constants/app-defaults";
import { SEND_TO_MYSELF } from "@constants/navigation";
import { CART_PAGE_PATHNAME } from "@constants/routes";
import { OLD_CART_PAGE_PATHNAME } from "@constants/old-routes";

import styles from "./main.module.scss";
import { GLOBAL_COUNTRY } from "@constants/country";
import { DEFAULT_COUNTRY_ISO } from "@stadium/constants";
import { CART_RESPONSE_TYPE } from "@constants/orders/cart";

const SendToManyCountrySelectorModal = () => {
  const router = useRouter();
  const { query, pathname: routerPathName, replace, asPath } = router;
  const { sendItems } = query;

  const dispatch = useDispatch();
  const userSelectedCountry = useSelector(getCountry);
  const orderId = useSelector(getCartOrderId);
  const cartProductZoneList = useSelector(getCartProductZoneList);

  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const { setSendItemsSelection, closeCountrySelectorModal, isSendToMyself } =
    useSendItemsContext();

  const { updateSelectedCountryItemsInLocalCart } = usePopulateLocalItems();

  const { allCountriesList } = useAllCountries({ includeGlobalCountry: !isSendToMyself });

  const { changeCountry } = useCountryChange(allCountriesList);
  const { openSwagVariantSelectorModal, getSwagVariantsSelectionProducts } =
    useCartListingContext();
  const countriesGroupedByZone = useConfig(getCountriesGroupedByZoneUsingCountryZoneId);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const defaultCountry = useMemo(() => {
    if (userSelectedCountry.iso === GLOBAL_COUNTRY.iso)
      return allCountriesList.find((country) => country.iso === DEFAULT_COUNTRY_ISO);
    return userSelectedCountry;
  }, [userSelectedCountry, allCountriesList]);

  const cartProductCountryDetailList = useMemo(
    () =>
      cartProductZoneList
        .map((zoneId) => countriesGroupedByZone[zoneId.toString()]?.[0] || {})
        .filter((country) => country.iso !== GLOBAL_COUNTRY.iso),
    [cartProductZoneList, countriesGroupedByZone],
  );

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [isUpdateBagLoading, setIsUpdateBagLoading] = useState(false);

  const updateOrderStatus = () => {
    if (isGuestOrPreviewMode) return updateSelectedCountryItemsInLocalCart(selectedCountry);
    return dispatch(
      updateOrderDetails(
        orderId,
        {
          program_id: SM_PROGRAM_ID,
          send_to_many: false,
          country_id: selectedCountry.id,
        },
        {},
        CART_RESPONSE_TYPE,
      ),
    );
  };

  const updateCart = (response) => {
    const order = formatOrder(response);
    dispatch(updateCartOrder(order));
  };

  const updateOrder = () => {
    setIsUpdateBagLoading(true);
    updateOrderStatus()
      .then((response) => {
        if (!isGuestOrPreviewMode) updateCart(response);
        setSendItemsSelection(SEND_TO_MYSELF);
        changeCountry(selectedCountry);
        closeCountrySelectorModal();
        if (
          !isGuestOrPreviewMode &&
          [CART_PAGE_PATHNAME, OLD_CART_PAGE_PATHNAME].includes(routerPathName)
        ) {
          const productList = getSwagVariantsSelectionProducts();
          productList.length > 0 && setTimeout(openSwagVariantSelectorModal, 0);
        }
        if (sendItems) {
          const trimmedPath = asPath.split("?")[0];
          replace(asPath, trimmedPath, { shallow: true });
        }
      })
      .finally(() => setIsUpdateBagLoading(false));
  };

  const renderCountrySelector = () => (
    <div className={styles.countryFieldGroup}>
      <label htmlFor="countryId" className={styles.labelName}>
        Country
      </label>
      <CountrySelect
        countries={cartProductCountryDetailList}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        type="round"
        labelText=""
        customClassName={styles.countryInputGroup}
      />
    </div>
  );

  const renderCountrySelectorModal = () => {
    if (cartProductZoneList.length < 2) {
      closeCountrySelectorModal();
      return null;
    }

    return (
      <Modal closeModal={closeCountrySelectorModal} newModal mobileBottomDrawer>
        <ModalDialog className={styles.sendToMyselfModal}>
          <ModalContent>
            <ModalHeader>
              <div>Change to &lsquo;Send to Myself&rsquo;</div>
              <CloseButton onClick={closeCountrySelectorModal} />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
              <div className={styles.separation16px}>
                To send items to yourself, select a country to continue with below. Your bag for
                additional countries will be emptied.
              </div>
              <div className={styles.separation32px}>
                You need to select sizes and colors for all swag items.
              </div>

              {renderCountrySelector()}
            </ModalBody>
            <ModalFooter>
              <InternalButton
                isLoading={isUpdateBagLoading}
                styleType="primary"
                onClick={updateOrder}
              >
                {isMobileDevice ? "select country & change" : "Update Bag"}
              </InternalButton>
            </ModalFooter>
          </ModalContent>
        </ModalDialog>
      </Modal>
    );
  };

  return renderCountrySelectorModal();
};
export default SendToManyCountrySelectorModal;
